import React, { useImperativeHandle, forwardRef } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentIntent } from '@stripe/stripe-js';

export interface CheckoutPaymentHandle {
  confirmPayment: () => Promise<{ paymentIntent?: PaymentIntent; error?: string }>;
}

interface CheckoutPaymentProps {
  onPaymentReady?: (isReady: boolean) => void;
  error?: string;
}

const CheckoutPayment = forwardRef<CheckoutPaymentHandle, CheckoutPaymentProps>(({
  onPaymentReady,
  error,
}, ref) => {
  const stripe = useStripe();
  const elements = useElements();

  useImperativeHandle(ref, () => ({
    confirmPayment: async () => {
      if (!stripe || !elements) {
        return { error: 'Payment system not initialized' };
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        return { error: submitError.message || 'Payment validation failed' };
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/confirmation`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        return { error: confirmError.message || 'Payment failed' };
      }

      return { paymentIntent };
    },
  }), [stripe, elements]);

  const handlePaymentChange = (event: { complete: boolean; empty: boolean }) => {
    onPaymentReady?.(event.complete);
  };

  return (
    <div className="checkout-payment">
      <p className="checkout-section-description">
        Enter your payment details to secure your booking.
      </p>

      {error && (
        <div className="message message--error">
          {error}
        </div>
      )}

      <div className="stripe-payment-element">
        <PaymentElement
          onChange={handlePaymentChange}
          options={{
            layout: 'tabs',
          }}
        />
      </div>
    </div>
  );
});

CheckoutPayment.displayName = 'CheckoutPayment';

export default CheckoutPayment;
